import { CustomerOrderBase } from '../contracts/customer-order.js';
import { EventTicketOrder } from '../models/serializable/event-ticket-order.js';
import { OnlineEventPassOrder } from '../models/serializable/online-event-pass-order.js';
import { GenericOrder } from '../models/serializable/orders/generic-order.js';
import { GenericOrderTicketEntry } from '../models/serializable/orders/generic-order-entries/generic-order-ticket-entry.js';
import { GenericOrderProductEntry } from '../models/serializable/orders/generic-order-entries/generic-order-product-entry.js';
import { GenericOrderVoucherEntry } from '../models/serializable/orders/generic-order-entries/generic-order-voucher-entry.js';
import { GenericOrderDonationEntry } from '../models/serializable/orders/generic-order-entries/generic-order-donation-entry.js';
import { GenericOrderTicketSupplementEntry } from '../models/serializable/orders/generic-order-entries/generic-order-ticket-supplement-entry.js';
import { OrderSource } from '../models/serializable/order-source.js';
import { PaymentMethod } from '../models/serializable/payment-method.js';
import { TicketTemplateChannel } from '../models/serializable/ticket-template/ticket-template-channel.js';
import { TicketPermission } from '../permissions/ticket-permission.js';
import { TicketTemplateChannelHelper } from './ticket-template-channel-helper.js';

export type AdminPaymentMethodOrder = EventTicketOrder | GenericOrder | OnlineEventPassOrder;

export interface AdminPaymentMethodPermission {
  readonly eventId: string | null;
  readonly permission: TicketPermission;
}

const SELLING_PERMISSION: Record<TicketTemplateChannel, TicketPermission> = {
  [TicketTemplateChannel.Online]: TicketPermission.OnlineTicketSelling,
  [TicketTemplateChannel.Admin]: TicketPermission.OfflineTicketSelling,
  [TicketTemplateChannel.Promoter]: TicketPermission.PromoterTicketSelling,
  [TicketTemplateChannel.Protocol]: TicketPermission.ProtocolTicketSelling,
};

/** A payment method is shared by every entry in an admin purchase. */
export class AdminOrderPaymentMethodHelper {
  static isSupported(order: CustomerOrderBase): order is AdminPaymentMethodOrder {
    return EventTicketOrder.assertType(order) || GenericOrder.assertType(order) || OnlineEventPassOrder.assertType(order);
  }

  static canChange(order: CustomerOrderBase): order is AdminPaymentMethodOrder {
    return this.isSupported(order) && order.source === OrderSource.Admin && order.isOpen &&
      order.paymentMethod != null && this.permissions(order) != null;
  }

  static permissions(order: AdminPaymentMethodOrder): readonly AdminPaymentMethodPermission[] | null {
    if(OnlineEventPassOrder.assertType(order)) {
      return [{ eventId: null, permission: TicketPermission.PassSelling }];
    }

    const permissions: AdminPaymentMethodPermission[] = [];
    const addTickets = (tickets: EventTicketOrder['tickets']): boolean => {
      for(const ticket of tickets) {
        const channel = TicketTemplateChannelHelper.getChannel(ticket.template);
        if(channel == null || ticket.serialNumber == null) return false;
        permissions.push({ eventId: ticket.eventId, permission: SELLING_PERMISSION[channel] });
      }
      return tickets.length > 0;
    };

    if(EventTicketOrder.assertType(order)) {
      return addTickets(order.tickets) ? permissions : null;
    }

    for(const entry of order.entries) {
      if(GenericOrderTicketEntry.assertType(entry)) {
        if(!addTickets(entry.tickets)) return null;
      }
      else if(GenericOrderProductEntry.assertType(entry) || GenericOrderTicketSupplementEntry.assertType(entry)) {
        permissions.push({ eventId: entry.eventId, permission: TicketPermission.ProductSelling });
      }
      else if(GenericOrderVoucherEntry.assertType(entry)) {
        permissions.push({ eventId: entry.eventId, permission: TicketPermission.PassSelling });
      }
      else if(GenericOrderDonationEntry.assertType(entry)) {
        permissions.push({ eventId: null, permission: TicketPermission.Donations });
      }
      else {
        return null;
      }
    }

    return permissions.length > 0 ? permissions : null;
  }

  static isPaymentMethod(value: unknown): value is PaymentMethod {
    return Object.values(PaymentMethod).some(method => method === value);
  }
}
